import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';

import './App.css';

import Popup from './components/Popup';
import usePopup from './hooks/usePopup';

import MainLayout from './components/layout/MainLayout';
import Home from './pages/Home';
import Media from './pages/Media';
import ServiceSingle from './pages/ServiceSingle';
import LoginScreen from './pages/LoginScreen';
import AdminPanel from './pages/AdminPanel';
import AdminDashboard from './pages/admin/AdminDashboard';
import DistrictManagement from './pages/DistrictManagement';
import DistrictInfo from './pages/DistrictInfo';
import IndustryManagement from './pages/IndustryManagement';
import IndustryInfo from './pages/IndustryInfo';
import IndustryPage from './components/industry/IndustryPage';
import InvestorRequestManagement from './pages/InvestorRequestManagement';
import CompanyManagement from './pages/admin/CompanyManagement';
import AssociationManagement from './pages/admin/AssociationManagement';
import AcademiaManagement from './pages/admin/AcademiaManagement';
import StartupManagement from './pages/admin/StartupManagement';
import IncubatorManagement from './pages/admin/IncubatorManagement';
import InvestorManagement from './pages/admin/InvestorManagement';
import ConsultantManagement from './pages/admin/ConsultantManagement';
import TopUtilityManagement from './pages/admin/TopUtilityManagement';
import PopupManagement from './pages/admin/PopupManagement';
import GalleryManagement from './pages/admin/GalleryManagement';
import PressReleaseManagement from './pages/admin/PressReleaseManagement';
import BlogHighlightManagement from './pages/admin/BlogHighlightManagement';
import BlogPostManagement from './pages/admin/BlogPostManagement';

import PostManagement from './pages/admin/PostManagement';
import PolicyManagement from './pages/admin/PolicyManagement';
import PolicyForm from './components/admin/policies/PolicyForm';
import ContactSubmissions from './pages/admin/ContactSubmissions';
import MembersManagement from './pages/MembersManagement';
import SlidersManagement from './pages/SlidersManagement';
import RegisterType from './components/auth/RegisterType';
import CompanyRegister from './components/auth/CompanyRegister';
import AssociationRegister from './components/auth/AssociationRegister';
import Dashboard from './components/dashboard/Dashboard';
import DashboardHome from './components/dashboard/DashboardHome';
import BusinessSuiteCatalog from './components/dashboard/BusinessSuiteCatalog';
import Profile from './components/dashboard/Profile';
import SocialMediaPage from './pages/SocialMediaPage';
import AboutAID from './pages/AboutAID';
import VisionMission from './pages/VisionMission';
import Members from './pages/Members';
import PressRelease from './pages/PressRelease';
import BlogListPage from './pages/BlogListPage';
import BlogDetailPage from './pages/BlogDetailPage';
// import PolicyRepository from './components/investor/PolicyRepository';
// import PolicyAdvocacy from './pages/PolicyAdvocacy';

import Business from './pages/Business';
import Investors from './pages/Investors';
import InvestorPage from './pages/InvestorPage';
import VidarbhaPage from './pages/VidarbhaPage';
import VidarbhaGlobalFoundation from './pages/VidarbhaGlobalFoundation';
import ContactUs from './pages/ContactUs';
import ContactUsForm from './pages/ContactUsForm';
import PolicyAdvocacy from './pages/PolicyAdvocacy';
import MahaGovPolicy from './pages/MahaGovPolicy';
import CentGovPolicy from './pages/CentGovPolicy';
import SchemeData from './pages/Scheme';
import CompanyDetails from './pages/CompanyDetails';
import StartupDetails from './pages/StartupDetails';
import IncubatorDetails from './pages/IncubatorDetails';
import AssociationDetails from './pages/AssociationDetails';
import InvestorDetails from './pages/InvestorDetails';
import AcademiaDetails from './pages/AcademiaDetails';
import ConsultantDetails from './pages/ConsultantDetails';
import InnovatorPage from './pages/InnovatorPage';
import OnlineLearning from './pages/OnlineLearning';
import JobOpenings from './pages/JobOpenings';
import ContactAID from './components/dashboard/ContactAID';
import AssociationProfile from './components/dashboard/AssociationProfile';
import AcademiaProfile from './components/dashboard/AcademiaProfile';
import StartupProfile from './components/dashboard/StartupProfile';
import IncubatorProfile from './components/dashboard/IncubatorProfile';
import ConsultantProfile from './components/dashboard/ConsultantProfile';
import InvestorProfile from './components/dashboard/InvestorProfile';

// Protected Route Component
const PrivateRoute = ({ children, adminOnly = false }) => {
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('userInfo') || '{}');

  if (!token) {
    return <Navigate to="/login" state={{ from: window.location.pathname }} />;
  }

  // For admin-only routes, check if user has admin or superadmin role
  if (adminOnly && user.role && !['admin', 'superadmin'].includes(user.role)) {
    // If user is not admin but has a role, redirect to their dashboard
    if (user.role === 'company') {
      return <Navigate to="/dashboard" />;
    }
    // Default redirect for other roles
    return <Navigate to="/" />;
  }

  return children;
};

// Wrapper component for routes that should use the main layout
const MainLayoutWrapper = ({ element }) => (
  <MainLayout>
    {element}
  </MainLayout>
);

function App() {
  const { currentPopup, closePopup } = usePopup();

  return (
    <Router>
      <div className="App min-h-screen">
        <ToastContainer />
        {/* Render popup if available */}
        {currentPopup && (
          <Popup popup={currentPopup} onClose={closePopup} />
        )}
        <Routes>
          {/* Routes with MainLayout */}
          <Route path="/" element={<MainLayoutWrapper element={<Home />} />} />
          <Route path="/business" element={<MainLayoutWrapper element={<Business />} />} />
          <Route path="/investor" element={<MainLayoutWrapper element={<InvestorPage />} />} />
          {/* <Route path="/servicesingle" element={<MainLayoutWrapper element={<ServiceSingle />} />} /> */}
          <Route path="/about-aid" element={<MainLayoutWrapper element={<AboutAID />} />} />
          <Route path="/aid-social" element={<MainLayoutWrapper element={<SocialMediaPage />} />} />
          <Route path="/vision-mission" element={<MainLayoutWrapper element={<VisionMission />} />} />
          <Route path="/members" element={<MainLayoutWrapper element={<Members />} />} />
          <Route path="/district/:id" element={<MainLayoutWrapper element={<DistrictInfo />} />} />
          <Route path="/industrypage/:id" element={<MainLayoutWrapper element={<IndustryPage />} />} />

          {/* <Route path="/policies" element={<MainLayoutWrapper element={<PolicyRepository />} />} />
          <Route path="/policy-repository" element={<MainLayoutWrapper element={<PolicyRepository />} />} /> */}
          <Route path="/policy-advocacy" element={<MainLayoutWrapper element={<PolicyAdvocacy />} />} />
          <Route path="/maha-gov-policy" element={<MainLayoutWrapper element={<MahaGovPolicy />} />} />
          <Route path="/cent-gov-policy" element={<MainLayoutWrapper element={<CentGovPolicy />} />} />
          <Route path="/investors" element={<MainLayoutWrapper element={<Investors />} />} />

          <Route path="/media-room" element={<MainLayoutWrapper element={<Media />} />} />
          <Route path="/vidarbha" element={<MainLayoutWrapper element={<VidarbhaPage />} />} />
          <Route path="/vidarbha-global-foundation" element={<MainLayoutWrapper element={<VidarbhaGlobalFoundation />} />} />
          <Route path="/contact-us" element={<MainLayoutWrapper element={<ContactUs />} />} />
          <Route path="/press-release" element={<MainLayoutWrapper element={<PressRelease />} />} />
          <Route path="/contactusform" element={<MainLayoutWrapper element={<ContactUsForm />} />} />
          <Route path="/portfolio" element={<MainLayoutWrapper element={<h1>Portfolio Page - Coming Soon</h1>} />} />
          <Route path="/blog" element={<MainLayoutWrapper element={<BlogListPage />} />} />
          <Route path="/blog/:slug" element={<MainLayoutWrapper element={<BlogDetailPage />} />} />
          <Route path="/contact" element={<MainLayoutWrapper element={<h1>Contact Page - Coming Soon</h1>} />} />
          <Route path="/job-openings" element={<MainLayoutWrapper element={<JobOpenings />} />} />
          <Route path="/learning-resources" element={<MainLayoutWrapper element={<OnlineLearning />} />} />

          <Route path="/association" element={<MainLayoutWrapper element={<h3 style={{ marginTop: "50px", marginBottom: "50px", textAlign: "center" }}> Coming Soon</h3>} />} />
          {/* <Route path="/company_profile" element={<Profile />} /> */}

          <Route path="/incubator" element={<MainLayoutWrapper element={<h3 style={{ marginTop: "50px", marginBottom: "50px", textAlign: "center" }}> Coming Soon</h3>} />} />
          <Route path="/startup" element={<MainLayoutWrapper element={<h3 style={{ marginTop: "50px", marginBottom: "50px", textAlign: "center" }}> Coming Soon</h3>} />} />
          <Route path="/scheme" element={<MainLayoutWrapper element={<SchemeData />} />} />
          <Route path="/company/:id" element={<MainLayoutWrapper element={<CompanyDetails />} />} />
          <Route path="/startup/:id" element={<MainLayoutWrapper element={<StartupDetails />} />} />
          <Route path="/incubator/:id" element={<MainLayoutWrapper element={<IncubatorDetails />} />} />
          <Route path="/association/:id" element={<MainLayoutWrapper element={<AssociationDetails />} />} />
          <Route path="/investor/:id" element={<MainLayoutWrapper element={<InvestorDetails />} />} />
          <Route path="/academia/:id" element={<MainLayoutWrapper element={<AcademiaDetails />} />} />
          <Route path="/consultant/:id" element={<MainLayoutWrapper element={<ConsultantDetails />} />} />
          <Route path="/innovator/:tab" element={<MainLayoutWrapper element={<InnovatorPage />} />} />
          <Route path="/innovator" element={<MainLayoutWrapper element={<InnovatorPage />} />} />

          {/* Routes without MainLayout */}
          <Route path="/login" element={<LoginScreen />} />
          <Route path="/register" element={<RegisterType />} />
          <Route path="/register/company" element={<CompanyRegister />} />
          <Route path="/register/association" element={<AssociationRegister />} />

          {/* Dashboard Routes */}
          {/* Dashboard Routes */}
          <Route path="/dashboard" element={<Dashboard />}>
            <Route index element={<div className="p-6"><h2 className="text-xl font-semibold mb-4">Dashboard Overview</h2></div>} />
            <Route path="company_profile" element={<Profile />} />
            <Route path="association_profile" element={<AssociationProfile />} />
            <Route path="academia_profile" element={<AcademiaProfile />} />
            <Route path="startup_profile" element={<StartupProfile />} />
            <Route path="incubator_profile" element={<IncubatorProfile />} />
            <Route path="consultant_profile" element={<ConsultantProfile />} />
            <Route path="investor_profile" element={<InvestorProfile />} />
            <Route path="contact" element={<ContactAID />} />
            <Route path="business-suite" element={<BusinessSuiteCatalog />} />
            {/* <Route path="company_homepage" element={<DashboardHome />} /> */}
            <Route path="products" element={<div className="p-6"><h2 className="text-xl font-semibold mb-4">Product Catalog</h2></div>} />
            <Route path="settings" element={<div className="p-6"><h2 className="text-xl font-semibold mb-4">Settings</h2></div>} />
          </Route>

          {/* Admin Routes - Only accessible to admin and superadmin */}
          <Route path="/admin" element={
            <PrivateRoute adminOnly={true}>
              <AdminPanel />
            </PrivateRoute>
          }>
            <Route index element={<AdminDashboard />} />
            <Route path="companies" element={<CompanyManagement />} />
            <Route path="associations" element={<AssociationManagement />} />
            <Route path="academia" element={<AcademiaManagement />} />
            <Route path="startups" element={<StartupManagement />} />
            <Route path="incubators" element={<IncubatorManagement />} />
            <Route path="investors" element={<InvestorManagement />} />
            <Route path="consultants" element={<ConsultantManagement />} />
            <Route path="districts" element={<DistrictManagement />} />
            <Route path="industries" element={<IndustryManagement />} />
            <Route path="investor-requests" element={<InvestorRequestManagement />} />
            <Route path="members" element={<MembersManagement />} />
            <Route path="sliders" element={<SlidersManagement />} />
            <Route path="popups" element={<PopupManagement />} />
            <Route path="gallery" element={<GalleryManagement />} />
            <Route path="press-releases" element={<PressReleaseManagement />} />
            <Route path="blog-highlights" element={<BlogHighlightManagement />} />
            <Route path="blog-posts" element={<BlogPostManagement />} />

            <Route path='posts' element={<PostManagement />} />



            <Route path="top-utility" element={<TopUtilityManagement />} />
            <Route path="contact-submissions" element={<ContactSubmissions />} />
            <Route path="policies">
              <Route index element={<PolicyManagement />} />
              <Route path="new" element={<PolicyForm />} />
              <Route path=":id" element={<PolicyForm isEdit={true} />} />
            </Route>
          </Route>

          {/* 404 Route */}
          <Route path="*" element={<MainLayoutWrapper element={<h1>404 - Page Not Found</h1>} />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;